import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import formatDistanceToNow from "date-fns/formatDistanceToNow";
import ApiService from "../../services/api-service";
import { getUserDetail } from "../../constants/function";
import notifier from "../../components/Notifier";

const GOLD = "#FFEE20";

function authorName(user) {
  if (!user) return "Anonymous";
  return `${user.firstName || ""} ${user.lastName || ""}`.trim() || "Anonymous";
}

function timeAgo(value) {
  try {
    return formatDistanceToNow(new Date(value), { addSuffix: true });
  } catch {
    return "";
  }
}

// Comment thread for a single gallery photo. Self-contained: fetches, posts,
// edits, deletes, and likes comments. `onCountChange(delta)` lets the parent
// keep its header count in sync without re-fetching.
export default function GalleryComments({ galleryId, onCountChange }) {
  const currentUser = getUserDetail();

  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");

  const [anchorEl, setAnchorEl] = useState(null);
  const [selected, setSelected] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchComments = useCallback(
    async (pageNum = 1) => {
      if (!galleryId) return;
      setLoading(true);
      try {
        const res = await ApiService.GALLERY_LIST_COMMENTS(galleryId, { page: pageNum, limit: 20 });
        const payload = (res && (res.result || res.data)) || {};
        const list = payload.comments || [];
        setComments((prev) => (pageNum === 1 ? list : [...prev, ...list]));
        setHasMore(Boolean(payload.pagination && payload.pagination.hasNextPage));
        setPage(pageNum);
      } catch (e) {
        notifier.error("Failed to load comments");
      } finally {
        setLoading(false);
      }
    },
    [galleryId]
  );

  useEffect(() => {
    fetchComments(1);
  }, [fetchComments]);

  const handleSubmit = async () => {
    const content = newComment.trim();
    if (!content || submitting) return;
    setSubmitting(true);
    try {
      const res = await ApiService.GALLERY_ADD_COMMENT(galleryId, content);
      const created = (res && (res.result || res.data)) || null;
      if (created) {
        // Backend populates the author, but fall back to the local user so the
        // new comment renders a name/avatar even if population is skipped.
        const withUser = {
          ...created,
          user: created.user || {
            _id: currentUser && currentUser._id,
            firstName: currentUser && currentUser.firstName,
            lastName: currentUser && currentUser.lastName,
            profileImg: currentUser && currentUser.profileImg,
          },
        };
        setComments((prev) => [withUser, ...prev]);
        setNewComment("");
        if (onCountChange) onCountChange(1);
      }
    } catch (e) {
      notifier.error("Failed to post comment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveEdit = async (commentId) => {
    const content = editText.trim();
    if (!content) return;
    try {
      await ApiService.GALLERY_EDIT_COMMENT(commentId, content);
      setComments((prev) =>
        prev.map((c) =>
          c._id === commentId ? { ...c, content, isEdited: true, editedAt: new Date() } : c
        )
      );
      setEditingId(null);
      setEditText("");
    } catch (e) {
      notifier.error("Failed to edit comment");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await ApiService.GALLERY_DELETE_COMMENT(deleteTarget._id);
      setComments((prev) => prev.filter((c) => c._id !== deleteTarget._id));
      if (onCountChange) onCountChange(-1);
    } catch (e) {
      notifier.error("Failed to delete comment");
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleToggleLike = async (comment) => {
    const liked = !comment.isLiked;
    // Optimistic update; revert on failure.
    setComments((prev) =>
      prev.map((c) =>
        c._id === comment._id
          ? { ...c, isLiked: liked, likesCount: Math.max(0, (c.likesCount || 0) + (liked ? 1 : -1)) }
          : c
      )
    );
    try {
      await ApiService.GALLERY_TOGGLE_COMMENT_LIKE(comment._id);
    } catch (e) {
      setComments((prev) =>
        prev.map((c) =>
          c._id === comment._id
            ? { ...c, isLiked: comment.isLiked, likesCount: comment.likesCount || 0 }
            : c
        )
      );
    }
  };

  const isOwner = (comment) =>
    currentUser && comment.user && String(currentUser._id) === String(comment.user._id);

  const goToProfile = (userId) => {
    if (userId) window.location.href = `/artist/${userId}`;
  };

  return (
    <Box>
      {/* Add a comment */}
      {currentUser ? (
        <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
          <Avatar src={currentUser?.profileImg?.filePath} sx={{ width: 36, height: 36 }} />
          <Box sx={{ flex: 1 }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              size="small"
              placeholder="Add a comment…"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              sx={{
                "& .MuiOutlinedInput-root": { bgcolor: "#1a1a1a", color: "#fff", borderRadius: "10px" },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#2a2a2a" },
              }}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}>
              <Button
                size="small"
                endIcon={submitting ? <CircularProgress size={14} sx={{ color: "#000" }} /> : <SendIcon />}
                onClick={handleSubmit}
                disabled={!newComment.trim() || submitting}
                sx={{
                  background: GOLD,
                  color: "#000",
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "8px",
                  "&:hover": { background: "#FFE066" },
                  "&.Mui-disabled": { background: "#333", color: "#777" },
                }}
              >
                Post
              </Button>
            </Box>
          </Box>
        </Box>
      ) : (
        <Typography sx={{ color: "#888", mb: 2, fontSize: 14 }}>Sign in to comment</Typography>
      )}

      {/* Comments list */}
      {loading && comments.length === 0 ? (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <CircularProgress size={22} sx={{ color: GOLD }} />
        </Box>
      ) : comments.length === 0 ? (
        <Typography sx={{ color: "#777", textAlign: "center", py: 3, fontSize: 14 }}>
          No comments yet. Be the first.
        </Typography>
      ) : (
        <>
          {comments.map((comment) => (
            <Box key={comment._id} sx={{ display: "flex", gap: 1.5, mb: 2 }}>
              <Avatar
                src={comment.user?.profileImg?.filePath}
                sx={{ width: 36, height: 36, cursor: "pointer" }}
                onClick={() => goToProfile(comment.user?._id)}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    sx={{ fontSize: 14, fontWeight: 600, color: "#fff", cursor: "pointer" }}
                    onClick={() => goToProfile(comment.user?._id)}
                  >
                    {authorName(comment.user)}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "#888" }}>
                    {timeAgo(comment.createdAt)}
                    {comment.isEdited ? " (edited)" : ""}
                  </Typography>
                </Box>

                {editingId === comment._id ? (
                  <Box sx={{ mt: 0.5 }}>
                    <TextField
                      fullWidth
                      multiline
                      size="small"
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      sx={{
                        "& .MuiOutlinedInput-root": { bgcolor: "#1a1a1a", color: "#fff", borderRadius: "10px" },
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "#2a2a2a" },
                      }}
                    />
                    <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                      <Button
                        size="small"
                        onClick={() => {
                          setEditingId(null);
                          setEditText("");
                        }}
                        sx={{ color: "#aaa", textTransform: "none" }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleSaveEdit(comment._id)}
                        disabled={!editText.trim()}
                        sx={{ background: GOLD, color: "#000", textTransform: "none", "&:hover": { background: "#FFE066" } }}
                      >
                        Save
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <>
                    <Typography sx={{ fontSize: 14, color: "#ddd", mt: 0.25, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                      {comment.content}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}>
                      <IconButton size="small" onClick={() => handleToggleLike(comment)} sx={{ color: comment.isLiked ? "#ff4d6d" : "#888" }}>
                        {comment.isLiked ? <FavoriteIcon sx={{ fontSize: 16 }} /> : <FavoriteBorderIcon sx={{ fontSize: 16 }} />}
                      </IconButton>
                      {comment.likesCount > 0 && (
                        <Typography sx={{ fontSize: 12, color: "#888" }}>{comment.likesCount}</Typography>
                      )}
                      {isOwner(comment) && (
                        <IconButton
                          size="small"
                          sx={{ color: "#888", ml: "auto" }}
                          onClick={(e) => {
                            setAnchorEl(e.currentTarget);
                            setSelected(comment);
                          }}
                        >
                          <MoreVertIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      )}
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          ))}

          {hasMore && (
            <Box sx={{ textAlign: "center", mt: 1 }}>
              <Button
                onClick={() => fetchComments(page + 1)}
                disabled={loading}
                sx={{ color: GOLD, textTransform: "none" }}
              >
                {loading ? "Loading…" : "Load more"}
              </Button>
            </Box>
          )}
        </>
      )}

      {/* Owner menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{ sx: { bgcolor: "#1a1a1a", color: "#fff" } }}
      >
        <MenuItem
          onClick={() => {
            setEditingId(selected._id);
            setEditText(selected.content);
            setAnchorEl(null);
          }}
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDeleteTarget(selected);
            setAnchorEl(null);
          }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1, color: "#ff5252" }} /> Delete
        </MenuItem>
      </Menu>

      {/* Delete confirmation */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        PaperProps={{ sx: { bgcolor: "#12121A", color: "#fff" } }}
      >
        <DialogTitle>Delete comment</DialogTitle>
        <DialogContent>This action cannot be undone.</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} sx={{ color: "#aaa", textTransform: "none" }}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained" sx={{ textTransform: "none" }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
